import React, { useState, useEffect } from 'react';
import { Scale, Printer, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);
const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };

const TYPE_COLORS = {
  asset:     'text-blue-700',
  liability: 'text-red-600',
  equity:    'text-purple-700',
};

// ── Date helpers — Fiscal Year starts Jan 1 ───────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based

function getBSDate(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();

  switch (preset) {
    case 'today':
      return iso(y, m, d);
    case 'end_of_month':
      return iso(y, m, lastDay(y, m));
    case 'end_of_last_month': {
      const lm = m === 1 ? 12 : m - 1;
      const ly = m === 1 ? y - 1 : y;
      return iso(ly, lm, lastDay(ly, lm));
    }
    case 'end_of_last_quarter': {
      const q = Math.ceil(m / 3);
      const pq = q === 1 ? 4 : q - 1;
      const py = q === 1 ? y - 1 : y;
      const qEnds = { 1: [3, 31], 2: [6, 30], 3: [9, 30], 4: [12, 31] };
      return iso(py, qEnds[pq][0], qEnds[pq][1]);
    }
    case 'end_of_last_fy':
      return iso(y - 1, 12, 31);
    default:
      return null;
  }
}

const BS_PRESETS = [
  { value: 'today',               label: 'Today' },
  { value: 'end_of_month',        label: 'End of This Month' },
  { value: 'end_of_last_month',   label: 'End of Last Month' },
  { value: 'end_of_last_quarter', label: 'End of Last Quarter' },
  { value: 'end_of_last_fy',      label: 'End of Last Fiscal Year' },
  { value: 'custom',              label: 'Custom Date…' },
];

function BSSection({ title, rows, total, colorClass }) {
  return (
    <div>
      <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="px-6 py-3 text-xs text-slate-400 italic">No entries</div>
      ) : rows.map(r => (
        <div key={r.account_id} className="flex items-center justify-between px-6 py-2.5 hover:bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400 w-14">{r.account_code}</span>
            <span className="text-sm text-slate-700">{r.account_name}</span>
          </div>
          <span className={`font-mono text-sm font-medium ${colorClass}`}>{fmt(r.balance)}</span>
        </div>
      ))}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-800">Total {title}</span>
        <span className={`font-mono text-sm font-bold ${colorClass}`}>{fmt(total)}</span>
      </div>
    </div>
  );
}

export default function BalanceSheet() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const { pathname } = useLocation();
  const entitySlug = pathname.split('/')[2];
  const lockedEntity = ENTITY_MAP[entitySlug] || null;

  const [preset, setPreset]   = useState('today');
  const [asOf, setAsOf]       = useState(todayStr);
  const [entity, setEntity]   = useState(lockedEntity || 'All');
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);

  const runReport = async (date, ent) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/accounts/reports/balance-sheet?as_of=${date}&entity=${ent}`);
      setReport(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate report');
    }
    setLoading(false);
  };

  // Auto-run Today on mount
  useEffect(() => {
    runReport(todayStr, lockedEntity || 'All');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetChange = (val) => {
    setPreset(val);
    if (val !== 'custom') {
      const date = getBSDate(val);
      setAsOf(date);
      runReport(date, entity);
    }
  };

  const handleEntityChange = (val) => {
    setEntity(val);
    if (preset !== 'custom') {
      const date = getBSDate(preset);
      if (date) runReport(date, val);
    }
  };

  const isBalanced = report ? Math.abs(report.total_assets - report.total_liabilities_equity) < 0.01 : null;
  const presetLabel = BS_PRESETS.find(p => p.value === preset)?.label || '';

  return (
    <div className="p-6 fade-in" data-testid="balance-sheet-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Balance Sheet
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Assets, Liabilities &amp; Equity — {presetLabel}</p>
        </div>
        {report && (
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50"
            data-testid="print-bs-btn"
          >
            <Printer size={14} /> Print
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex gap-3 items-end mb-6 flex-wrap bg-white border border-slate-200 rounded-lg p-4">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">As of</label>
          <select
            value={preset}
            onChange={e => handlePresetChange(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
            data-testid="bs-preset-select"
          >
            {BS_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {preset === 'custom' && (
          <>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date</label>
              <input
                type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
                data-testid="bs-custom-date"
              />
            </div>
            <button
              onClick={() => runReport(asOf, entity)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="generate-bs-btn"
            >
              {loading ? <><RefreshCw size={14} className="animate-spin" /> Generating…</> : 'Generate'}
            </button>
          </>
        )}

        {!lockedEntity && (
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Entity</label>
            <select
              value={entity}
              onChange={e => handleEntityChange(e.target.value)}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900"
              data-testid="bs-entity-select"
            >
              <option value="All">All Entities</option>
              <option value="Eyvo USA">Eyvo USA</option>
              <option value="Eyvo India">Eyvo India</option>
            </select>
          </div>
        )}

        {loading && preset !== 'custom' && (
          <div className="flex items-center gap-2 text-sm text-slate-500 self-end pb-2">
            <RefreshCw size={14} className="animate-spin" /> Generating…
          </div>
        )}
      </div>

      {/* Report */}
      {report && (
        <div className="space-y-0 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="balance-sheet-content">
          <div className="px-6 py-5 border-b border-slate-200 text-center">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {report.entity === 'All' ? 'Consolidated' : report.entity} — Balance Sheet
            </h2>
            <p className="text-sm text-slate-500 mt-1">As of {report.as_of}</p>
          </div>

          <BSSection title="Assets"      rows={report.assets}      total={report.total_assets}      colorClass={TYPE_COLORS.asset} />
          <BSSection title="Liabilities" rows={report.liabilities} total={report.total_liabilities} colorClass={TYPE_COLORS.liability} />
          <BSSection title="Equity"      rows={report.equity}      total={report.total_equity}      colorClass={TYPE_COLORS.equity} />

          <div className={`flex items-center justify-between px-6 py-4 border-t-2 border-slate-300 ${isBalanced ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <div className="flex items-center gap-2">
              {isBalanced
                ? <CheckCircle2 size={16} className="text-emerald-600" />
                : <AlertCircle  size={16} className="text-red-600" />}
              <span className="text-sm font-semibold text-slate-700">
                {isBalanced ? 'Balance Sheet is balanced' : 'Warning: Balance Sheet is not balanced'}
              </span>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Total Liabilities + Equity</p>
              <p className="font-mono text-base font-bold text-slate-900">{fmt(report.total_liabilities_equity)}</p>
            </div>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="bg-white border border-slate-200 rounded-lg px-5 py-16 text-center text-slate-400 text-sm">
          <Scale size={32} className="mx-auto mb-3 text-slate-300" />
          Select an as-of date above
        </div>
      )}
    </div>
  );
}
